import { useEffect, useState } from 'react';
import './GoogleAdsLanding.css';
import { isPreviousInquiryBlockedError, submitContactInquiry } from './contactApi';
import { scrollToHash } from './scrollToHash';

const HERO_PHRASES = [
  'Impulsa tu negocio.',
  'Impulsa tu empresa.',
  'Innovacion y desarrollo.'
];

const SERVICE_ITEMS = [
  {
    icon: 'web',
    title: 'Desarrollo web y movil',
    description: 'Aplicaciones enfocadas en conversion, rendimiento y crecimiento comercial.'
  },
  {
    icon: 'platform',
    title: 'Software a medida',
    description: 'Plataformas y paneles adaptados a tu operacion y a tus procesos reales.'
  },
  {
    icon: 'automation',
    title: 'Integraciones y automatizacion',
    description: 'Conexion entre sistemas, flujos automaticos y menos tareas manuales.'
  }
];

const SERVICE_ICONS = {
  web: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3.5" y="4" width="17" height="13.5" rx="2.5" />
      <path d="M3.5 8h17" />
      <path d="M8.5 12.2l-2.2 2.1 2.2 2.1" />
      <path d="M15.5 12.2l2.2 2.1-2.2 2.1" />
      <path d="M12.4 11.2l-1 6" />
    </svg>
  ),
  platform: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="4.5" width="16" height="5.2" rx="1.8" />
      <rect x="4" y="10.9" width="16" height="4.8" rx="1.8" />
      <rect x="4" y="17" width="16" height="2.5" rx="1.2" />
      <path d="M8 7.1h.01" />
      <path d="M8 13.3h.01" />
      <path d="M12 7.1h5" />
      <path d="M12 13.3h5" />
    </svg>
  ),
  automation: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 3.2v2.3" />
      <path d="M12 18.5v2.3" />
      <path d="M3.2 12h2.3" />
      <path d="M18.5 12h2.3" />
      <path d="M5.8 5.8l1.7 1.7" />
      <path d="M16.5 16.5l1.7 1.7" />
      <path d="M5.8 18.2l1.7-1.7" />
      <path d="M16.5 7.5l1.7-1.7" />
      <path d="M11 12l1.1 1.1 2.3-2.3" />
    </svg>
  )
};

const OFFER_ITEMS = [
  'Respuesta comercial rapida',
  'Analisis tecnico inicial sin costo',
  'Plan de trabajo claro y escalable'
];

const CONTACT_FORM_FEEDBACK = {
  sending: 'Enviando...',
  success: 'Consulta enviada. Te contactaremos pronto.',
  error: 'No se pudo enviar la consulta. Intentalo nuevamente.',
  blocked: 'La consulta fue bloqueada porque ya enviaron una previamente desde esta conexion.'
};

const WHATSAPP_URL = 'https://wa.me/5493416926584';
const GOOGLE_ADS_FORM_CONVERSION = {
  sendTo: 'AW-831277997/E1w5CMmLvYUcEK2XsYwD',
  value: 50000.0,
  currency: 'ARS'
};
const GOOGLE_ADS_HASH_SCROLL_OPTIONS = {
  extraOffset: 20
};

function reportGoogleAdsFormConversion(url) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return false;
  }

  const callback = () => {
    if (typeof url !== 'undefined') {
      window.location = url;
    }
  };

  window.gtag('event', 'conversion', {
    send_to: GOOGLE_ADS_FORM_CONVERSION.sendTo,
    value: GOOGLE_ADS_FORM_CONVERSION.value,
    currency: GOOGLE_ADS_FORM_CONVERSION.currency,
    event_callback: callback
  });

  return false;
}

function GoogleAdsLanding({ contactApiUrl, onWhatsAppClick }) {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState({ type: 'idle', message: '' });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const prefersReducedMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;

    if (prefersReducedMotion) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setPhraseIndex((prev) => (prev + 1) % HERO_PHRASES.length);
    }, 2600);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const scrollFromCurrentHash = () => {
      if (!window.location.hash) {
        return;
      }

      window.requestAnimationFrame(() => {
        scrollToHash(window.location.hash, {
          ...GOOGLE_ADS_HASH_SCROLL_OPTIONS,
          updateHash: false
        });
      });
    };

    const initialTimer = window.setTimeout(scrollFromCurrentHash, 0);
    window.addEventListener('hashchange', scrollFromCurrentHash);

    return () => {
      window.clearTimeout(initialTimer);
      window.removeEventListener('hashchange', scrollFromCurrentHash);
    };
  }, []);

  const activePhrase = HERO_PHRASES[phraseIndex];

  const handleWhatsAppClick = () => {
    if (typeof onWhatsAppClick === 'function') {
      onWhatsAppClick();
    }
  };

  const handleSectionLinkClick = (event) => {
    const href = event.currentTarget.getAttribute('href');

    if (!href?.startsWith('#')) {
      return;
    }

    event.preventDefault();

    if (typeof window === 'undefined') {
      return;
    }

    window.requestAnimationFrame(() => {
      scrollToHash(href, GOOGLE_ADS_HASH_SCROLL_OPTIONS);
    });
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();

    const formElement = event.currentTarget;

    if (!formElement.checkValidity()) {
      formElement.reportValidity();
      return;
    }

    const formData = new FormData(formElement);
    const name = String(formData.get('name') ?? '').trim();
    const company = String(formData.get('company') ?? '').trim();
    const phone = String(formData.get('phone') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const service = String(formData.get('service') ?? '').trim();
    const message = String(formData.get('message') ?? '').trim();
    const composedMessage = [
      service ? `Servicio de interes: ${service}.` : '',
      company ? `Empresa: ${company}.` : '',
      message
    ]
      .filter(Boolean)
      .join(' ');
    const payload = {
      nombre: name,
      telefono: phone,
      email,
      consulta: composedMessage
    };

    setIsSubmitting(true);
    setFormStatus({ type: 'idle', message: '' });

    try {
      await submitContactInquiry(contactApiUrl, payload);
      reportGoogleAdsFormConversion();
      formElement.reset();
      setFormStatus({ type: 'success', message: CONTACT_FORM_FEEDBACK.success });
    } catch (error) {
      console.error('No se pudo enviar la consulta desde la landing:', error);
      setFormStatus({
        type: 'error',
        message: isPreviousInquiryBlockedError(error)
          ? CONTACT_FORM_FEEDBACK.blocked
          : CONTACT_FORM_FEEDBACK.error
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="ads-landing">
      <header className="ads-top-strip">
        <div className="ads-top-strip__inner">
          <span>Desarrollo de software a medida</span>
          <span>Aplicaciones web y moviles</span>
          <span>Integraciones y automatizacion</span>
        </div>
      </header>

      <main className="ads-shell">
        <section className="ads-hero reveal is-visible">
          <p className="ads-kicker">Publicidad orientada a resultados</p>

          <div className="ads-hero-grid">
            <div className="ads-copy">
              <h1 className="ads-title">
                <span>Soluciones digitales para crecer.</span>
                <span className="ads-title-rotating" key={activePhrase} aria-live="polite">
                  {activePhrase}
                </span>
              </h1>
              <p className="ads-description">
                Coordinamos una reunion y te mostramos como mejorar ventas, procesos y presencia
                digital con una propuesta clara.
              </p>

              <div className="ads-actions">
                <a
                  className="ads-btn ads-btn--primary"
                  href="#ads-contact"
                  onClick={handleSectionLinkClick}
                >
                  Quiero una reunion
                </a>
                <a
                  className="ads-btn ads-btn--ghost"
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={handleWhatsAppClick}
                >
                  WhatsApp directo
                </a>
              </div>

              <div className="ads-phrase-row">
                {HERO_PHRASES.map((phrase) => (
                  <span className="ads-chip" key={phrase}>
                    {phrase}
                  </span>
                ))}
              </div>
            </div>

            <div className="ads-visual" aria-hidden="true">
              <span className="ads-visual-line" />
              <figure className="ads-circle ads-circle--left">
                <img src={`${process.env.PUBLIC_URL}/tecnoinicio.png`} alt="" />
              </figure>
              <figure className="ads-circle ads-circle--right">
                <img src={`${process.env.PUBLIC_URL}/software.png`} alt="" />
              </figure>
              <div className="ads-logo-core">
                <img src={`${process.env.PUBLIC_URL}/react.ico`} alt="Genius Software" />
                <span>Genius Software</span>
              </div>
            </div>
          </div>
        </section>

        <section className="ads-services reveal is-visible">
          <div className="ads-section-header">
            <p className="ads-section-eyebrow">Desarrollo de software a medida</p>
            <h2>Servicios listos para tu crecimiento</h2>
          </div>

          <div className="ads-services-grid">
            {SERVICE_ITEMS.map((item) => (
              <article className="ads-service-card" key={item.title}>
                <div className="ads-service-card__icon">{SERVICE_ICONS[item.icon]}</div>
                <div className="ads-service-card__content">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="ads-contact reveal is-visible" id="ads-contact">
          <div className="ads-contact-copy">
            <p className="ads-section-eyebrow">Contacto</p>
            <h2>Contanos tu objetivo y te asesoramos</h2>
            <p>
              Te respondemos con una propuesta concreta para impulsar tu negocio con tecnologia.
            </p>
            <ul className="ads-benefits">
              {OFFER_ITEMS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <form className="ads-form" onSubmit={handleContactSubmit}>
            <p className="ads-form-note">Todos los campos son obligatorios.</p>

            <label className="ads-field">
              <span className="ads-field__label">
                Nombre
                <span className="ads-required-mark" aria-hidden="true">
                  *
                </span>
              </span>
              <input
                type="text"
                name="name"
                placeholder="Tu nombre"
                autoComplete="name"
                required
              />
            </label>

            <label className="ads-field">
              <span className="ads-field__label">
                Empresa
                <span className="ads-required-mark" aria-hidden="true">
                  *
                </span>
              </span>
              <input
                type="text"
                name="company"
                placeholder="Nombre de tu empresa"
                autoComplete="organization"
                required
              />
            </label>

            <label className="ads-field">
              <span className="ads-field__label">
                Telefono
                <span className="ads-required-mark" aria-hidden="true">
                  *
                </span>
              </span>
              <input
                type="tel"
                name="phone"
                placeholder="+54 ..."
                autoComplete="tel"
                required
              />
            </label>

            <label className="ads-field">
              <span className="ads-field__label">
                Email
                <span className="ads-required-mark" aria-hidden="true">
                  *
                </span>
              </span>
              <input
                type="email"
                name="email"
                placeholder="tuemail@empresa.com"
                autoComplete="email"
                required
              />
            </label>

            <label className="ads-field ads-field--full">
              <span className="ads-field__label">
                Servicio de interes
                <span className="ads-required-mark" aria-hidden="true">
                  *
                </span>
              </span>
              <select name="service" defaultValue="" required>
                <option value="" disabled>
                  Selecciona una opcion
                </option>
                <option value="Desarrollo de software a medida">
                  Desarrollo de software a medida
                </option>
                <option value="Aplicacion web o movil">Aplicacion web o movil</option>
                <option value="Automatizacion e integraciones">Automatizacion e integraciones</option>
                <option value="Consultoria tecnologica">Consultoria tecnologica</option>
              </select>
            </label>

            <label className="ads-field ads-field--full">
              <span className="ads-field__label">
                Mensaje
                <span className="ads-required-mark" aria-hidden="true">
                  *
                </span>
              </span>
              <textarea
                name="message"
                rows="4"
                placeholder="Contanos brevemente que necesitas"
                required
              />
            </label>

            {formStatus.message ? (
              <p
                className={`ads-form-status ${
                  formStatus.type === 'success' ? 'is-success' : 'is-error'
                }`}
                role={formStatus.type === 'error' ? 'alert' : 'status'}
              >
                {formStatus.message}
              </p>
            ) : null}

            <button
              className="ads-btn ads-btn--primary ads-btn--submit"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? CONTACT_FORM_FEEDBACK.sending : 'Enviar consulta'}
            </button>
          </form>
        </section>
      </main>

      <a
        className="ads-whatsapp-fab"
        href={WHATSAPP_URL}
        target="_blank"
        rel="noreferrer"
        aria-label="Abrir chat de WhatsApp"
        onClick={handleWhatsAppClick}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 4a8 8 0 0 0-6.9 12.1L4 20l3.9-1A8 8 0 1 0 12 4z" />
          <path d="M9.4 9.6c.2-.4.4-.4.6-.4h.5c.2 0 .4.1.5.3l.7 1.5c.1.2.1.4 0 .6l-.4.5a.4.4 0 0 0 0 .5c.4.7 1.1 1.4 1.9 1.9a.4.4 0 0 0 .5 0l.5-.4c.2-.1.4-.1.6 0l1.5.7c.2.1.3.3.3.5v.5c0 .2 0 .4-.2.6-.2.2-.8.6-1.8.6-1.2 0-2.7-.6-3.9-1.7-1.2-1.1-2-2.6-2-3.8 0-.9.3-1.5.5-1.7z" />
        </svg>
        <span>WhatsApp</span>
      </a>
    </div>
  );
}

export default GoogleAdsLanding;
